import {AbstractEditPage} from "../../../framework/entity/edit/edit-page/abstract-edit-page";
import {ChangeDetectorRef, Component, OnDestroy} from "@angular/core";
import {provideEntityModel, provideReportModel} from "@fedeltapointofsale/data-client";
import {SalesJournalEntityModel} from "../sales-journal-entity.model";
import {FUIAlertActions, FUIAlertService, FUIResponsiveService} from "@fedeltapointofsale/ui";
import {
    SalesJournalVoidAction,
    SalesJournalVoidSaleAction,
    SalesJournalVoidSaleService
} from "../sales-journal-void-sale.service";
import {Subscription} from "rxjs";
import {SalesJournalVoidCashoutService} from "../sales-journal-void-cashout.service";
import {SalesJournalVoidSurchargeService} from "../sales-journal-void-surcharge.service";
import {SalesJournalVoidDiscountService} from "../sales-journal-void-discount.service";
import {Plugin, ServerAuth} from "../../../config";
import {ActivatedRoute} from "@angular/router";
import {SalesJournalSearchPageComponent} from "../sales-journal-search-page/sales-journal-search-page.component";
import {InvoiceReportModel} from "../../reports/invoice-report/invoice-report.model";
import {SalesJournalVoidTipService} from "../sales-journal-void-tip.service";

@Component({
    selector: "sales-journal-edit-page",
    templateUrl: "./sales-journal-edit-page.component.html",
    styleUrls: ["./sales-journal-edit-page.component.scss"],
    providers: [provideReportModel(InvoiceReportModel), provideEntityModel(SalesJournalEntityModel),
        SalesJournalVoidSaleService, SalesJournalVoidSurchargeService, SalesJournalVoidCashoutService,
        SalesJournalVoidDiscountService, SalesJournalVoidTipService]
})
export class SalesJournalEditPageComponent extends AbstractEditPage implements OnDestroy {
    Plugin = Plugin;
    ServerAuth = ServerAuth

    private subscriptions: Subscription = new Subscription();

    saleVoided: boolean;

    hasDiscount: boolean;
    hasSurcharge: boolean;
    hasTip: boolean;
    hasCashout: boolean;

    overrideSearchRoute: string;

    constructor(public model: SalesJournalEntityModel,
                private alertService: FUIAlertService,
                private voidSaleService: SalesJournalVoidSaleService,
                private voidCashoutService: SalesJournalVoidCashoutService,
                private voidSurchargeService: SalesJournalVoidSurchargeService,
                private voidDiscountService: SalesJournalVoidDiscountService,
                private voidTipService: SalesJournalVoidTipService,
                private invoiceReportModel: InvoiceReportModel,
                activatedRoute: ActivatedRoute,
                changeDetectorRef: ChangeDetectorRef,
                responsiveService: FUIResponsiveService) {
        super(changeDetectorRef, responsiveService);
        this.subscriptions.add(this.model.$data.subscribe(() => {
            this.saleVoided = this.model.data.saleLines.find(value => value.type === "VOID") != undefined;
            this.hasCashout = this.model.data.payments.reduce((total, value) => total + (value.cashout | 0), 0) > 0;
            this.hasTip = this.model.data.payments.reduce((total, value) => total + (value.tip | 0), 0) > 0;
            this.hasDiscount = this.model.data.saleDiscount.dollarAmount != undefined && this.model.data.saleDiscount.dollarAmount !== 0;
            this.hasSurcharge = this.model.data.saleSurcharge != undefined && this.model.data.saleSurcharge !== 0;
        }));

        activatedRoute.data.subscribe((data) => {
            if (data["maintenance"]) {
                this.overrideSearchRoute = SalesJournalSearchPageComponent.MAINTENANCE_SEARCH_PAGE;
            }
        });
    }

    voidSale(): void {
        this.alertService.showConfirmationYesNoCancel("wbo.sales-journal.edit.prompt.void.heading",
            "wbo.sales-journal.edit.prompt.void.message").subscribe((action) => {
            if (action === FUIAlertActions.YES_ACTION || action === FUIAlertActions.NO_ACTION) {
                this.voidSaleService.performAction(new SalesJournalVoidSaleAction(this.model.data["receiptNo"], action === FUIAlertActions.YES_ACTION)).then(() => {
                    this.editPageComponent.reload();
                    this.changeDetectorRef.detectChanges();
                });
            }
        });
    }

    voidSurcharge(): void {
        this.alertService.showConfirmationOkCancel("wbo.sales-journal.edit.prompt.void-surcharge.heading",
            "wbo.sales-journal.edit.prompt.void-surcharge.message").subscribe((action) => {
            if (action === FUIAlertActions.OK_ACTION) {
                this.voidSurchargeService.performAction(new SalesJournalVoidAction(this.model.data["receiptNo"])).then(() => {
                    this.editPageComponent.reload();
                    this.changeDetectorRef.detectChanges();
                });
            }
        });
    }

    voidCashout(): void {
        this.alertService.showConfirmationOkCancel("wbo.sales-journal.edit.prompt.void-cashout.heading",
            "wbo.sales-journal.edit.prompt.void-cashout.message").subscribe((action) => {
            if (action === FUIAlertActions.OK_ACTION) {
                this.voidCashoutService.performAction(new SalesJournalVoidAction(this.model.data["receiptNo"])).then(() => {
                    this.editPageComponent.reload();
                    this.changeDetectorRef.detectChanges();
                });
            }
        });
    }

    voidDiscount(): void {
        this.alertService.showConfirmationOkCancel("wbo.sales-journal.edit.prompt.void-discount.heading",
            "wbo.sales-journal.edit.prompt.void-discount.message").subscribe((action) => {
            if (action === FUIAlertActions.OK_ACTION) {
                this.voidDiscountService.performAction(new SalesJournalVoidAction(this.model.data["receiptNo"])).then(() => {
                    this.editPageComponent.reload();
                    this.changeDetectorRef.detectChanges();
                });
            }
        });
    }

    voidTip(): void {
        this.alertService.showConfirmationOkCancel("wbo.sales-journal.edit.prompt.void-tip.heading",
            "wbo.sales-journal.edit.prompt.void-tip.message").subscribe((action) => {
            if (action === FUIAlertActions.OK_ACTION) {
                this.voidTipService.performAction(new SalesJournalVoidAction(this.model.data["receiptNo"])).then(() => {
                    this.editPageComponent.reload();
                    this.changeDetectorRef.detectChanges();
                });
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    override canDeactivate(): boolean {
        return true;
    }

    generateReceipt(): void {
        if (!this.editPageComponent.reportGenerationService.running) {
            this.invoiceReportModel.data.receiptNo = this.model.data.receiptNo;
            this.invoiceReportModel.data.outletId = this.model.data.outletId;
            this.editPageComponent.reportGenerationService.execute(this.invoiceReportModel);
        }
    }

    get showActions(): boolean {
        return this.model.data.isAppOrder || !(this.model.data.isSaleOnOpenFloat || this.model.data.isSaleFromApi);
    }

    openIntegratedDeliveryTrackingLink(): void {
        window.open(this.model.data.integratedDeliveryTrackingUrl);
    }
}
